/**
 * Where the browser keeps its crypto state.
 *
 * Three things live here: the account pickle (this device's Olm identity),
 * Olm session pickles keyed by the peer's curve25519 key, and inbound Megolm
 * session pickles keyed by session id. All of them are vodozemac pickles,
 * already encrypted under the pickle key before they arrive.
 *
 * ## About the pickle key
 *
 * On a phone this key sits in the Keychain or Keystore, which script cannot
 * read. A browser has no equivalent, so the next best thing is what we do
 * here: an AES-GCM key generated **non-extractable**, stored as a CryptoKey
 * object in IndexedDB, and used to wrap the pickle key.
 *
 * Be clear about what that buys. A stolen IndexedDB dump - disk forensics, a
 * backup, another OS account reading the profile directory - yields wrapped
 * bytes and an opaque key handle that cannot be exported. It does NOT stop
 * script running on this origin: an XSS can still *use* the key to unwrap.
 * That is a real ceiling, not an oversight, and it is the reason a browser
 * should never be the only place a conversation exists.
 */
const DB_NAME = 'nsibi-crypto';
// Bump this whenever a store is added. A browser that already opened the
// database at an older version never runs `onupgradeneeded` again, so the new
// store simply does not exist and every write to it throws
// "One of the specified object stores was not found" - which surfaced as
// messages failing to send, because caching the plaintext is part of that
// path. Adding a store without bumping is the whole failure.
//   v1 -> meta, olm, megolm, outbound
//   v2 -> + plaintext
//   v3 -> + messages (local-first transcript cache)
const DB_VERSION = 3;

const STORE_META = 'meta'; // wrapping key, wrapped pickle key, account pickle
const STORE_OLM = 'olm'; // peer curve25519 -> session pickle
const STORE_MEGOLM = 'megolm'; // session_id -> inbound session pickle
const STORE_OUTBOUND = 'outbound'; // channel_id -> our sending session
const STORE_PLAINTEXT = 'plaintext'; // message_id -> decrypted body
const STORE_MESSAGES = 'messages'; // channel_id -> decrypted message rows

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      // Creates whatever is missing rather than assuming a fresh database:
      // this runs for a first install and for every version bump, and an
      // existing browser arrives here holding the older set.
      const db = req.result;
      for (const name of [
        STORE_META,
        STORE_OLM,
        STORE_MEGOLM,
        STORE_OUTBOUND,
        STORE_PLAINTEXT,
        STORE_MESSAGES,
      ]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

const get = <T>(store: string, key: string) =>
  tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
const put = (store: string, key: string, value: unknown) =>
  tx(store, 'readwrite', (s) => s.put(value, key) as IDBRequest<IDBValidKey>);

/* ── the pickle key ──────────────────────────────────────────────────── */

const KEY_WRAPPER = 'wrapping-key';
const KEY_WRAPPED = 'pickle-key';

async function wrappingKey(): Promise<CryptoKey> {
  const existing = await get<CryptoKey>(STORE_META, KEY_WRAPPER);
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);
  // Non-extractable, so what lands in IndexedDB is a handle rather than key
  // material. Structured clone stores the CryptoKey itself.
  await put(STORE_META, KEY_WRAPPER, key);
  return key;
}

const b64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));

/**
 * Re-wrap stored bytes in a fresh view.
 *
 * A `Uint8Array` read back out of IndexedDB is typed over `ArrayBufferLike`,
 * which WebCrypto's `BufferSource` will not accept because it could in
 * principle be a `SharedArrayBuffer`. Copying gives a view over a real
 * `ArrayBuffer` and costs twelve bytes.
 */
const bufferSource = (bytes: Uint8Array): Uint8Array<ArrayBuffer> =>
  new Uint8Array(bytes);

/** The base64 pickle key vodozemac expects, minted once per browser. */
export async function pickleKey(): Promise<string> {
  const key = await wrappingKey();
  const stored = await get<{ iv: Uint8Array; data: ArrayBuffer }>(STORE_META, KEY_WRAPPED);

  if (stored) {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: bufferSource(stored.iv) },
      key,
      stored.data,
    );
    return new TextDecoder().decode(plain);
  }

  const fresh = b64(crypto.getRandomValues(new Uint8Array(32)));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: bufferSource(iv) },
    key,
    new TextEncoder().encode(fresh),
  );
  await put(STORE_META, KEY_WRAPPED, { iv, data });
  return fresh;
}

/* ── account ─────────────────────────────────────────────────────────── */

const KEY_ACCOUNT = 'account-pickle';

export const loadAccount = () => get<string>(STORE_META, KEY_ACCOUNT);
export const saveAccount = (pickle: string) => put(STORE_META, KEY_ACCOUNT, pickle);

/* ── sessions ────────────────────────────────────────────────────────── */

export const loadOlmSession = (peerIdentityKey: string) =>
  get<string>(STORE_OLM, peerIdentityKey);
export const saveOlmSession = (peerIdentityKey: string, pickle: string) =>
  put(STORE_OLM, peerIdentityKey, pickle);

export const loadMegolmSession = (sessionId: string) =>
  get<string>(STORE_MEGOLM, sessionId);
export const saveMegolmSession = (sessionId: string, pickle: string) =>
  put(STORE_MEGOLM, sessionId, pickle);

/**
 * The session we encrypt WITH for one channel.
 *
 * `sharedWith` records which devices already hold the key, as
 * "userId:deviceId" tags. Without it every send would re-share to everyone,
 * which is a to-device message per member device per message.
 */
export type OutboundSession = {
  sessionId: string;
  pickle: string;
  /**
   * The key a recipient needs, kept from creation.
   *
   * It cannot be recovered from the pickle through the current binding, and
   * it is what a device joining later must be given so it can read the
   * messages already sent under this session.
   */
  sessionKey: string;
  sharedWith: string[];
};

export const loadOutboundSession = (channelId: string) =>
  get<OutboundSession>(STORE_OUTBOUND, channelId);
export const saveOutboundSession = (channelId: string, row: OutboundSession) =>
  put(STORE_OUTBOUND, channelId, row);

/* ── small flags ─────────────────────────────────────────────────────── */

/** Whether this browser has already asked the phone for its history.
 *  Lives with the keys, so clearing the store clears it too and a fresh
 *  sign-in asks again. */
const KEY_HISTORY_REQUESTED = 'history-requested';
export const historyRequested = () => get<boolean>(STORE_META, KEY_HISTORY_REQUESTED);
export const markHistoryRequested = () => put(STORE_META, KEY_HISTORY_REQUESTED, true);

/** Everything, gone. Called on sign-out: a signed-out browser must not keep
 *  the keys to a conversation it can no longer legitimately read. */
export async function clearCryptoStore(): Promise<void> {
  const db = await openDb();
  await Promise.all(
    [STORE_META, STORE_OLM, STORE_MEGOLM, STORE_OUTBOUND, STORE_PLAINTEXT, STORE_MESSAGES].map(
      (name) =>
        new Promise<void>((resolve, reject) => {
          const t = db.transaction(name, 'readwrite');
          const req = t.objectStore(name).clear();
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        }),
    ),
  );
}


/* ── decrypted bodies ────────────────────────────────────────────────── */

/**
 * Plaintext we have already recovered, by message id.
 *
 * The chat list needs a preview of each conversation's last message, and the
 * server cannot supply one: on an E2EE channel it holds only ciphertext, and
 * `my-channels` returns an empty `content` with no envelope to decrypt. The
 * phone solves this from its own SQLite store of decrypted messages; this is
 * the browser's equivalent.
 *
 * A cache, not a source of truth. Browser storage is evictable in a way a
 * phone database is not, so a missing entry must degrade to a neutral
 * preview rather than an error.
 */
export const loadPlaintext = (messageId: string) =>
  get<string>(STORE_PLAINTEXT, messageId);

export const savePlaintext = (messageId: string, body: string) =>
  put(STORE_PLAINTEXT, messageId, body);

/** Bulk read for the chat list, which wants many at once. */
export async function loadPlaintexts(
  messageIds: string[],
): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  await Promise.all(
    messageIds.map(async (id) => {
      const body = await loadPlaintext(id);
      if (body !== undefined) out[id] = body;
    }),
  );
  return out;
}


/* ── local transcript cache ──────────────────────────────────────────────
 * The decrypted rows for a conversation, so opening it renders instantly from
 * the device instead of waiting on (and visibly depending on) the server -
 * the WhatsApp behaviour where your history is simply there. The server list
 * is still fetched in the background to reconcile, but it is no longer what
 * the transcript waits for. Rows are stored already-decrypted (plaintext on
 * the row), which is fine: this is the user's own device.
 */
export const loadChannelMessages = (channelId: string) =>
  get<unknown[]>(STORE_MESSAGES, channelId);

export const saveChannelMessages = (channelId: string, rows: unknown[]) =>
  put(STORE_MESSAGES, channelId, rows);
